// Cache manager for images
// Handles image-level caching for S3 URLs

// S3 URL cache: hash -> S3 URL (for individual images)
const s3UrlCache = new Map<string, string>();

// Generate SHA256 hash of buffer (browser Web Crypto API)
export async function hashBuffer(buffer: Uint8Array | ArrayBuffer): Promise<string> {
  let arrayBuffer: ArrayBuffer;
  if (buffer instanceof ArrayBuffer) {
    arrayBuffer = buffer;
  } else if (buffer instanceof Uint8Array) {
    // Create a new ArrayBuffer from the Uint8Array to avoid SharedArrayBuffer issues
    arrayBuffer = buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength) as ArrayBuffer;
  } else {
    throw new TypeError('Invalid buffer type');
  }
  
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Load S3 URL cache from localStorage
export function loadS3CacheFromStorage(): void {
  try {
    const cacheData = localStorage.getItem('s3UrlCache');
    if (cacheData) {
      const cache = JSON.parse(cacheData);
      Object.entries(cache).forEach(([hash, url]) => {
        s3UrlCache.set(hash, url as string);
      });
      console.log(`Loaded ${s3UrlCache.size} cached S3 URLs from localStorage`);
    }
  } catch (error) {
    console.warn('Failed to load S3 cache from localStorage:', error);
  }
}

// Save S3 URL cache to localStorage
export function saveS3CacheToStorage(): void {
  try {
    const cacheObj: Record<string, string> = {};
    s3UrlCache.forEach((url, hash) => {
      cacheObj[hash] = url;
    });
    localStorage.setItem('s3UrlCache', JSON.stringify(cacheObj));
  } catch (error) {
    console.warn('Failed to save S3 cache to localStorage:', error);
  }
}

// Get cached S3 URL for image hash
export function getCachedImageUrl(imageHash: string): string | null {
  return s3UrlCache.get(imageHash) || null;
}

// Save S3 URL to cache
export function saveImageUrlToCache(imageHash: string, url: string): void {
  s3UrlCache.set(imageHash, url);
  saveS3CacheToStorage();
}

// Get cache statistics
export function getCacheStats(): {
  images: number;
  imageCacheSize: number;
} {
  let imageCacheSize = 0;
  try {
    const cacheData = localStorage.getItem('s3UrlCache');
    if (cacheData) {
      imageCacheSize = new Blob([cacheData]).size;
    }
  } catch (e) {
    console.debug('Error calculating image cache size:', e);
  }
  
  return {
    images: s3UrlCache.size,
    imageCacheSize
  };
}

// Clear all caches
export function clearAllCaches(): void {
  s3UrlCache.clear();
  localStorage.removeItem('s3UrlCache');
  console.log('🗑️ All caches cleared');
}

// Initialize caches on module load
loadS3CacheFromStorage();
